import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import masterconfigAxios from "../services/masterconfigAxios";
import { MC_VERSION } from "../shared/constants/apiEndpoints";

type HistoryKind = "parameters" | "controls";

interface HistoryRow {
  id: string;
  version: string;
  description: string;
  original_filename: string;
  upload_date: string | null;
  active: boolean;
  latest: boolean;
  status: number;
  status_label: string;
  parent_id: string | null;
}

const fileExt = (name?: string) => {
  if (!name || !name.includes(".")) return "";
  return name.split(".").pop()?.toUpperCase() || "";
};

/**
 * History screen — GET /version?history=parameters|controls
 */
const MasterDataVersionHistory: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const brand = isDarkMode ? "#818CF8" : "#4F46E5";
  const pageBg = isDarkMode ? "#09090B" : "#F5F3FF";
  const cardBg = isDarkMode ? "#18181B" : "#FFFFFF";
  const borderClr = isDarkMode ? "rgba(255,255,255,0.10)" : "#E9E4FF";
  const primaryText = isDarkMode ? "#F4F4F5" : "#1E1B4B";
  const secondaryText = isDarkMode ? "#A1A1AA" : "#6B7280";
  const mutedBg = isDarkMode ? "#27272A" : "#F8F7FC";
  const successBg = isDarkMode ? "rgba(34,197,94,.15)" : "#ECFDF3";
  const successText = isDarkMode ? "#86EFAC" : "#059669";
  const failBg = isDarkMode ? "rgba(239,68,68,.15)" : "#FEF3F2";
  const failText = isDarkMode ? "#FCA5A5" : "#B42318";
  const warnBg = isDarkMode ? "rgba(245,158,11,.15)" : "#FFFAEB";
  const warnText = isDarkMode ? "#FCD34D" : "#B54708";

  const kind: HistoryKind =
    searchParams.get("type") === "controls" ? "controls" : "parameters";
  const title =
    kind === "controls"
      ? "Controls version history"
      : "Parameters version history";

  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await masterconfigAxios.get(MC_VERSION, {
        params: { history: kind },
      });
      const payload = res.data?.data ?? res.data;
      const list = payload?.history?.[kind];
      setRows(Array.isArray(list) ? list : []);
      setPage(1);
    } catch (err: any) {
      setRows([]);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load version history"
      );
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate();
    const monthName = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day} ${monthName} ${year}, ${hours}:${minutes}`;
  };

  const statusStyles = (label: string) => {
    const key = (label || "").toLowerCase();
    if (key === "success") return { bg: successBg, color: successText };
    if (key === "failed") return { bg: failBg, color: failText };
    return { bg: warnBg, color: warnText };
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all") {
        if ((row.status_label || "").toLowerCase() !== statusFilter) {
          return false;
        }
      }
      if (dateFilter) {
        if (!row.upload_date) return false;
        const d = new Date(row.upload_date);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        if (key !== dateFilter) return false;
      }
      if (!q) return true;
      return (
        row.version?.toLowerCase().includes(q) ||
        row.original_filename?.toLowerCase().includes(q) ||
        row.description?.toLowerCase().includes(q) ||
        row.id?.toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter, dateFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const safePage = Math.min(page, pageCount);
  const pagedRows = filteredRows.slice(
    (safePage - 1) * rowsPerPage,
    safePage * rowsPerPage
  );
  const from =
    filteredRows.length === 0 ? 0 : (safePage - 1) * rowsPerPage + 1;
  const to = Math.min(safePage * rowsPerPage, filteredRows.length);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setDateFilter("");
    setPage(1);
  };

  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success("Version ID copied");
    } catch {
      toast.error("Unable to copy");
    }
  };

  const statusOptions = useMemo(() => {
    const set = new Set(
      rows.map((r) => (r.status_label || "").toLowerCase()).filter(Boolean)
    );
    return ["all", ...Array.from(set)];
  }, [rows]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 103px)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: { xs: 1.5, md: 2.5 },
        borderRadius: 3,
        bgcolor: pageBg,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/master-data")}
            sx={{
              textTransform: "none",
              color: secondaryText,
              minWidth: 0,
              px: 1,
              fontWeight: 600,
            }}
          >
            Back
          </Button>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: isDarkMode ? "rgba(129,140,248,.18)" : "#EDE9FE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {kind === "controls" ? (
              <CategoryOutlinedIcon sx={{ color: brand }} />
            ) : (
              <DescriptionOutlinedIcon sx={{ color: brand }} />
            )}
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 800, fontSize: 20, color: primaryText }}
            >
              {title}
            </Typography>
            <Typography sx={{ fontSize: 13, color: secondaryText, mt: 0.2 }}>
              All published uploads for this package.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchHistory}
          disabled={loading}
          sx={{
            textTransform: "none",
            borderColor: borderClr,
            color: primaryText,
            bgcolor: cardBg,
            borderRadius: 2.5,
            fontWeight: 600,
            "&:hover": { borderColor: borderClr, bgcolor: mutedBg },
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2.5,
          bgcolor: cardBg,
          border: `1px solid ${borderClr}`,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search by version, file name, notes or version ID..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          sx={{
            flex: 1,
            minWidth: 220,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: mutedBg,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: secondaryText, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            displayEmpty
            sx={{ borderRadius: 2, bgcolor: mutedBg }}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt === "all"
                  ? "Status: All"
                  : opt.charAt(0).toUpperCase() + opt.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            setPage(1);
          }}
          sx={{
            minWidth: 160,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: mutedBg,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          startIcon={<ClearIcon />}
          onClick={clearFilters}
          sx={{
            textTransform: "none",
            color: secondaryText,
            fontWeight: 600,
          }}
        >
          Clear filters
        </Button>
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 3,
          bgcolor: cardBg,
          border: `1px solid ${borderClr}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={32} sx={{ color: brand }} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2.5 }}>
            <Alert
              severity="error"
              sx={{ borderRadius: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={fetchHistory}
                  sx={{ textTransform: "none" }}
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ flex: 1, overflow: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "VERSION",
                      "STATUS",
                      "FILE NAME",
                      "RELEASE NOTES",
                      "UPLOADED ON",
                      "VERSION ID",
                      "ACTIONS",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          bgcolor: mutedBg,
                          color: secondaryText,
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          borderBottom: `1px solid ${borderClr}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 6, textAlign: "center" }}>
                        <Typography sx={{ color: secondaryText }}>
                          No versions found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedRows.map((row) => {
                      const st = statusStyles(row.status_label);
                      const ext = fileExt(row.original_filename);
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            "& td": {
                              borderBottom: `1px solid ${borderClr}`,
                              py: 1.5,
                            },
                          }}
                        >
                          <TableCell>
                            <Typography
                              sx={{
                                fontFamily: "'Fira Code', monospace",
                                fontWeight: 700,
                                fontSize: 13,
                                color: brand,
                              }}
                            >
                              {row.version || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      bgcolor: st.color,
                                    }}
                                  />
                                  {row.status_label || "—"}
                                </Box>
                              }
                              sx={{
                                height: 24,
                                fontSize: 12,
                                fontWeight: 600,
                                bgcolor: st.bg,
                                color: st.color,
                                borderRadius: 999,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1.5,
                                  bgcolor: mutedBg,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: brand,
                                  flexShrink: 0,
                                }}
                              >
                                <InsertDriveFileOutlinedIcon
                                  sx={{ fontSize: 16 }}
                                />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Tooltip
                                  title={row.original_filename || ""}
                                  arrow
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: primaryText,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {row.original_filename || "—"}
                                  </Typography>
                                </Tooltip>
                                {ext && (
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      color: secondaryText,
                                      fontWeight: 600,
                                    }}
                                  >
                                    .{ext}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Tooltip
                              title={row.description || ""}
                              arrow
                              disableHoverListener={!row.description}
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  color: secondaryText,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.description?.trim() || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                color: secondaryText,
                              }}
                            >
                              <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />
                              <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                {formatDate(row.upload_date)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Tooltip title={row.id} arrow>
                              <Typography
                                sx={{
                                  fontFamily: "'Fira Code', monospace",
                                  fontSize: 11,
                                  color: secondaryText,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.id}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.25 }}>
                              <Tooltip title="Copy version ID">
                                <IconButton
                                  size="small"
                                  onClick={() => copyId(row.id)}
                                  sx={{ color: secondaryText }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                size="small"
                                disabled
                                sx={{ color: secondaryText }}
                              >
                                <MoreVertIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${borderClr}`,
              }}
            >
              <Typography sx={{ fontSize: 13, color: secondaryText }}>
                Showing {from} to {to} of {filteredRows.length} versions
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 13, color: secondaryText }}>
                    Rows per page
                  </Typography>
                  <FormControl size="small">
                    <Select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                      sx={{ borderRadius: 2, height: 32 }}
                    >
                      {[10, 20, 50].map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Pagination
                  count={pageCount}
                  page={safePage}
                  onChange={(_, p) => setPage(p)}
                  color="primary"
                  shape="rounded"
                  siblingCount={1}
                  boundaryCount={1}
                  sx={{
                    "& .Mui-selected": {
                      bgcolor: `${brand} !important`,
                    },
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MasterDataVersionHistory;
